import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectProps,
  SelectVariants,
} from '@mui/material';
import React, { useId } from 'react';
import { FieldPath, FieldValues, useController, UseControllerProps } from 'react-hook-form';

export type SelectControlProps<T extends FieldValues, TName extends FieldPath<T>> = UseControllerProps<T, TName> &
  Omit<SelectProps, 'defaultValue' | 'variant'> & {
    options?: { label: React.ReactNode; value: string | number; disabled?: boolean }[];
    helperText?: React.ReactNode;
    children?: React.ReactNode;
    dataTestId?: string;
    variant?: SelectVariants;
  };

export const SelectControl = <T extends FieldValues, TName extends FieldPath<T>>({
  name,
  label,
  rules,
  control,
  defaultValue,
  shouldUnregister,
  options,
  helperText,
  children,
  dataTestId,
  ...props
}: SelectControlProps<T, TName>): React.JSX.Element => {
  const { field, fieldState } = useController({ name, rules, control, defaultValue, shouldUnregister });
  const id = useId();

  const multipleProps = props.multiple
    ? {
        value: Array.isArray(field.value) ? field.value : [],
        // fall back to a default renderValue only when the caller hasn't provided one
        renderValue:
          props.renderValue ??
          ((selected: unknown) =>
            Array.isArray(selected)
              ? selected
                  .map((value: string | number) => {
                    const label = options?.find(option => option.value === value)?.label;
                    return typeof label === 'string' ? label : String(value);
                  })
                  .join(', ')
              : String(selected)),
        SelectDisplayProps: { style: { whiteSpace: 'normal' } },
      }
    : {};

  return (
    <FormControl
      data-test-id={dataTestId}
      fullWidth={props.fullWidth}
      margin={props.margin}
      required={props.required || !!rules?.required}
      error={fieldState.invalid}
      size={props.size}
    >
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select {...field} {...props} {...multipleProps} labelId={`${id}-label`} id={id} label={label}>
        {options?.map(option =>
          props.multiple ? (
            <MenuItem
              key={option.value}
              data-test-id={`option-${dataTestId}-${option.value}`}
              value={option.value}
              disabled={option.disabled}
            >
              <Checkbox checked={Array.isArray(field.value) && field.value.includes(option.value)} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ) : (
            <MenuItem
              key={option.value}
              data-test-id={`option-${dataTestId}-${option.value}`}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </MenuItem>
          ),
        )}
        {children}
      </Select>
      {helperText && !fieldState.error && <FormHelperText>{helperText}</FormHelperText>}
      {fieldState.error?.message && <FormHelperText>{fieldState.error.message}</FormHelperText>}
    </FormControl>
  );
};
